import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { values, filter } from 'lodash/fp';
import Modal from 'components/modal';
import Box from 'components/box';
import Button from 'components/button';
import { Text } from 'components/text';
import LoadingIndicator from 'components/loading-indicator';
import { Alert } from 'components/alerts';
import { fetchHouseholdMembers } from 'store/members/members-actions';
import { memberByIdSelector, membersKeyedByIdSelector } from 'store/members/members-selectors';
import MemberHouseholdDetails from './member-household-details';
import session from 'lib/session';
import { apiConfig } from 'lib/config';

class HouseholdMembersModal extends Component {
  static mapStateToProps = (state, ownProps) => {
    if (!ownProps.memberId) {
      return {
        householdMembers: [],
        isLoadingHouseholdMembers: false,
        householdMembersError: null,
      };
    }

    // Get all members in the household (including current member)
    const currentMember = memberByIdSelector(state, ownProps.memberId);
    const allMembers = values(membersKeyedByIdSelector(state));
    const householdMembers = currentMember && currentMember.householdId
      ? filter(m => m.householdId === currentMember.householdId)(allMembers)
      : [];

    return {
      householdMembers,
      isLoadingHouseholdMembers: state.members.isLoadingMembers,
      householdMembersError: state.members.membersError,
    };
  }

  static mapDispatchToProps = dispatch => ({
    loadHouseholdMembers: memberId => dispatch(fetchHouseholdMembers({ member_id: memberId })),
  })

  state = {
    isDeleting: false,
    deleteError: null,
  }

  componentDidMount() {
    const { memberId, loadHouseholdMembers } = this.props;
    if (memberId) {
      loadHouseholdMembers(memberId);
    }
  }

  handleDeleteMember = async (memberIdToDelete, fromHouseholdDetails = false) => {
    // Only refetch if deletion is from household details component
    if (!fromHouseholdDetails) {
      return;
    }

    this.setState({ isDeleting: true, deleteError: null });

    try {
      const tokenWithExpiry = session.getTokenWithExpiry();
      if (!tokenWithExpiry || !tokenWithExpiry.token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const baseUrl = apiConfig.BASE_URL;
      // Ensure member ID is a string
      const memberIdString = String(memberIdToDelete);
      
      const response = await fetch(`${baseUrl}/incorrect_members/bulk_delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${tokenWithExpiry.token}`,
        },
        body: JSON.stringify({ member_ids: [memberIdString] }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || errorData.message || `Delete failed: ${response.statusText}`;
        throw new Error(errorMessage);
      }

      const responseData = await response.json().catch(() => ({}));

      // Check if the deleted member was the current member (household head)
      // If so, the entire household was deleted, so close the modal
      const { memberId, onRequestClose } = this.props;
      const currentMemberIdString = String(memberId);
      
      if (memberIdString === currentMemberIdString) {
        // The household head was deleted, so the entire household is gone
        this.setState({ 
          isDeleting: false,
          deleteError: null,
        });
        // Close the modal after a short delay to show success
        setTimeout(() => {
          onRequestClose();
        }, 1000);
        return;
      }

      // Refresh the household members if the deleted member was not the current one
      this.setState({ 
        isDeleting: false,
        deleteError: null,
      });

      // Reload household members - wait a bit for the backend to process
      const { loadHouseholdMembers } = this.props;
      if (memberId) {
        // Small delay to ensure backend has processed the deletion
        setTimeout(() => {
          loadHouseholdMembers(memberId);
        }, 500);
      }
    } catch (error) {
      console.error('Delete error:', error);
      this.setState({ 
        isDeleting: false, 
        deleteError: error.message || 'Failed to delete member. Please try again.' 
      });
    }
  }

  renderFooter() {
    const { onRequestClose } = this.props;
    return (
      <Box flex justifyContent="flex-end">
        <Button small inline onClick={onRequestClose}>Close</Button>
      </Box>
    );
  }

  render() {
    const { onRequestClose, memberId, householdMembers, isLoadingHouseholdMembers, householdMembersError } = this.props;
    const { isDeleting, deleteError } = this.state;

    // Find the current member from household members to pass to MemberHouseholdDetails
    const currentMember = householdMembers.find(m => m.id === memberId);

    return (
      <Modal
        title="Household Members"
        onRequestClose={onRequestClose}
        footer={this.renderFooter()}
        maxWidth="800px"
      >
        {isLoadingHouseholdMembers && (
          <Box flex alignItems="center" flexDirection="column" paddingTop={4}>
            <LoadingIndicator noun="household members" title="Loading household members..." />
          </Box>
        )}
        {householdMembersError && (
          <Box paddingTop={4}>
            <Alert type="error">{householdMembersError}</Alert>
          </Box>
        )}
        {deleteError && (
          <Box paddingTop={4}>
            <Alert type="error">{deleteError}</Alert>
          </Box>
        )}
        {isDeleting && (
          <Box paddingTop={4}>
            <Text>Deleting member...</Text>
          </Box>
        )}
        {!isLoadingHouseholdMembers && !householdMembersError && !isDeleting && currentMember && (
          <MemberHouseholdDetails
            householdMembers={householdMembers}
            currentMember={currentMember}
            linkBaseUrl="/members"
            onDeleteMember={this.handleDeleteMember}
          />
        )}
        {!isLoadingHouseholdMembers && !householdMembersError && !isDeleting && !currentMember && householdMembers.length === 0 && (
          <Box paddingTop={4}>
            <Text>No household members found.</Text>
          </Box>
        )}
      </Modal>
    );
  }
}

HouseholdMembersModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
  memberId: PropTypes.string.isRequired,
  householdMembers: PropTypes.arrayOf(PropTypes.shape({})),
  isLoadingHouseholdMembers: PropTypes.bool,
  householdMembersError: PropTypes.string,
  loadHouseholdMembers: PropTypes.func.isRequired,
};

HouseholdMembersModal.defaultProps = {
  householdMembers: [],
  isLoadingHouseholdMembers: false,
  householdMembersError: null,
};

export default connect(
  HouseholdMembersModal.mapStateToProps,
  HouseholdMembersModal.mapDispatchToProps,
)(HouseholdMembersModal);

